import type { ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { hourLabel, moneyShort, number, seriesColor, shortDate } from '../lib/format.ts';

const AXIS_TICK = { fill: 'var(--text-muted)', fontSize: 11 };

/**
 * Legends are always present for two or more series, and up to four are also
 * direct-labeled at the end of their line, so identity never rests on color
 * alone. A single-series chart carries no legend - the card title names it.
 */
export function Legend({
  items,
  variant = 'line',
}: {
  items: Array<{ label: string; color: string }>;
  variant?: 'line' | 'block';
}) {
  if (items.length < 2) return null;
  return (
    <div className="legend">
      {items.map((item) => (
        <span className="legend-item" key={item.label}>
          <span
            className={`legend-swatch ${variant === 'line' ? 'line' : ''}`}
            style={{ background: item.color }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

interface TooltipRow {
  label: string;
  value: string;
  color?: string;
}

function TooltipShell({ title, rows }: { title: string; rows: TooltipRow[] }) {
  return (
    <div className="chart-tooltip">
      <div className="tt-title">{title}</div>
      {rows.map((row) => (
        <div className="tt-row" key={row.label}>
          <span className="k">
            {row.color && <span className="legend-swatch" style={{ background: row.color }} />}
            {row.label}
          </span>
          <span className="v">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

interface SeriesSpec {
  key: string;
  label: string;
  color: string;
}

/**
 * Revenue over time. One y-axis only - a second measure on a second scale would
 * invite a comparison the geometry does not support, so units and revenue live
 * in separate charts rather than sharing an axis.
 */
export function RevenueTrend({
  data,
  series,
  height = 260,
  formatValue = moneyShort,
  formatBucket = shortDate,
}: {
  data: Array<Record<string, number | string>>;
  series: SeriesSpec[];
  height?: number;
  /** Y axis and tooltip formatting. Defaults to money; pass `number` for counts. */
  formatValue?: (value: number) => string;
  /** X axis label formatting, so a month bucket need not read as a date. */
  formatBucket?: (bucket: string) => string;
}) {
  return (
    <>
      {/* One series needs no legend box - the card title already names it. */}
      {series.length > 1 && (
        <Legend items={series.map((s) => ({ label: s.label, color: s.color }))} />
      )}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 6, right: 12, bottom: 0, left: 4 }}>
          <defs>
            {series.map((s) => (
              <linearGradient id={`fill-${s.key}`} key={s.key} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid stroke="var(--grid)" vertical={false} />
          <XAxis
            dataKey="bucket"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: 'var(--axis)' }}
            tickFormatter={formatBucket}
            minTickGap={28}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={54}
            tickFormatter={formatValue}
          />
          <Tooltip
            cursor={{ stroke: 'var(--axis)', strokeWidth: 1 }}
            content={({ active, payload, label }) =>
              active && payload?.length ? (
                <TooltipShell
                  title={formatBucket(String(label))}
                  rows={payload.map((entry) => ({
                    label: series.find((s) => s.key === entry.dataKey)?.label ?? String(entry.name),
                    value: formatValue(Number(entry.value)),
                    color: String(entry.color),
                  }))}
                />
              ) : null
            }
          />
          {series.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              stroke={s.color}
              strokeWidth={2}
              fill={`url(#fill-${s.key})`}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface-1)' }}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </>
  );
}

/**
 * Price history for one item: the server-wide traded average each day, with the
 * subscriber's own realized price over the top.
 *
 * Both series are real - the market line is every completed trade on the
 * server, ours is the subset that was theirs - so the chart answers "am I
 * tracking the market" rather than implying our slice is the market.
 */
export function PriceHistory({
  points,
}: {
  points: Array<{ day: string; median: number; samples: number; yours: number | null }>;
}) {
  const hasOwn = points.some((p) => p.yours != null);
  const series = [{ label: 'Market average', color: seriesColor(0) }];
  if (hasOwn) series.push({ label: 'Your price', color: seriesColor(1) });

  return (
    <>
      <Legend items={series} />
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={points} margin={{ top: 6, right: 12, bottom: 0, left: 4 }}>
          <CartesianGrid stroke="var(--grid)" vertical={false} />
          <XAxis
            dataKey="day"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: 'var(--axis)' }}
            tickFormatter={shortDate}
            minTickGap={30}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={54}
            tickFormatter={moneyShort}
          />
          <Tooltip
            cursor={{ stroke: 'var(--axis)' }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const point = payload[0]?.payload as {
                median: number;
                samples: number;
                yours: number | null;
              };
              const rows: TooltipRow[] = [
                { label: 'Market average', value: moneyShort(point.median), color: seriesColor(0) },
              ];
              if (point.yours != null) {
                rows.push({
                  label: 'Your price',
                  value: moneyShort(point.yours),
                  color: seriesColor(1),
                });
              }
              rows.push({ label: 'Trades server-wide', value: number(point.samples) });
              return <TooltipShell title={shortDate(String(label))} rows={rows} />;
            }}
          />
          <Line
            type="monotone"
            dataKey="median"
            stroke={seriesColor(0)}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface-1)' }}
          />
          {hasOwn && (
            <Line
              type="monotone"
              dataKey="yours"
              stroke={seriesColor(1)}
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={false}
              connectNulls
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
}

/**
 * Horizontal ranked bars for the opportunity finder. One series, so no legend;
 * the value is direct-labeled on each bar because the point of the chart is the
 * order, not precise magnitude reading off an axis.
 */
export function RankedBars({
  data,
  valueLabel,
  formatValue,
}: {
  data: Array<{ name: string; value: number; hint?: string }>;
  valueLabel: string;
  formatValue: (value: number) => string;
}) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, data.length * 30 + 30)}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 4, right: 56, bottom: 4, left: 4 }}
        barCategoryGap={6}
      >
        <CartesianGrid stroke="var(--grid)" horizontal={false} />
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="name"
          tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          width={150}
        />
        <Tooltip
          cursor={{ fill: 'var(--surface-2)' }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const point = payload[0]?.payload as { name: string; value: number; hint?: string };
            return (
              <TooltipShell
                title={point.name}
                rows={[
                  { label: valueLabel, value: formatValue(point.value), color: seriesColor(0) },
                  ...(point.hint ? [{ label: 'Why', value: point.hint }] : []),
                ]}
              />
            );
          }}
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} fill={seriesColor(0)} maxBarSize={18}>
          {data.map((entry) => (
            <Cell key={entry.name} fill={seriesColor(0)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

const SEQ_STEPS = [
  'var(--seq-100)',
  'var(--seq-200)',
  'var(--seq-300)',
  'var(--seq-400)',
  'var(--seq-500)',
  'var(--seq-600)',
  'var(--seq-700)',
] as const;

/**
 * Demand heatmap: weekday x hour, sequential single-hue blue.
 *
 * Magnitude is encoded by lightness on one hue, never by a rainbow. An empty
 * cell is drawn as the page surface with a hairline rather than the palest blue,
 * so "no sales in this hour" reads as absence instead of as a small value - the
 * distinction the restock and demand advice both hang on. Every cell carries a
 * title so the exact figure is available on hover and to a screen reader.
 */
export function DemandHeatmap({
  cells,
  weekdayNames,
}: {
  cells: Array<{ weekday: number; hour: number; units: number; revenue: number }>;
  weekdayNames: readonly string[];
}) {
  const max = Math.max(0, ...cells.map((c) => c.units));

  const stepFor = (units: number): string | null => {
    if (units <= 0 || max <= 0) return null;
    const ratio = units / max;
    const index = Math.min(SEQ_STEPS.length - 1, Math.floor(ratio * SEQ_STEPS.length));
    return SEQ_STEPS[index]!;
  };

  return (
    <div className="table-scroll">
      <div className="heatmap">
        <div />
        {Array.from({ length: 24 }, (_, hour) => (
          <div className="hm-hour" key={`h-${hour}`}>
            {hour % 3 === 0 ? hourLabel(hour) : ''}
          </div>
        ))}
        {weekdayNames.map((name, weekday) => (
          <FragmentRow
            key={name}
            name={name}
            weekday={weekday}
            cells={cells}
            stepFor={stepFor}
          />
        ))}
      </div>
      <div className="heatmap-scale" style={{ marginTop: 10 }}>
        <span>No sales</span>
        <span className="steps">
          <span style={{ background: 'var(--surface-2)', border: '1px solid var(--grid)' }} />
          {SEQ_STEPS.map((step) => (
            <span key={step} style={{ background: step }} />
          ))}
        </span>
        <span>{number(max)} units</span>
      </div>
    </div>
  );
}

function FragmentRow({
  name,
  weekday,
  cells,
  stepFor,
}: {
  name: string;
  weekday: number;
  cells: Array<{ weekday: number; hour: number; units: number; revenue: number }>;
  stepFor: (units: number) => string | null;
}) {
  return (
    <>
      <div className="hm-label">{name}</div>
      {Array.from({ length: 24 }, (_, hour) => {
        const cell = cells.find((c) => c.weekday === weekday && c.hour === hour);
        const units = cell?.units ?? 0;
        const step = stepFor(units);
        return (
          <div
            key={`${weekday}-${hour}`}
            className={`hm-cell ${step ? '' : 'zero'}`}
            style={step ? { background: step } : undefined}
            title={`${name} ${hourLabel(hour)} - ${number(units)} units, ${moneyShort(cell?.revenue ?? 0)}`}
          />
        );
      })}
    </>
  );
}

/** Server-wide activity backdrop. One series, normalised 0-1, so no legend. */
export function ActivityCurve({ curve }: { curve: number[] }) {
  const data = curve.map((value, hour) => ({ hour, value: Math.round(value * 100) }));
  return (
    <ResponsiveContainer width="100%" height={130}>
      <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="fill-activity" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={seriesColor(0)} stopOpacity={0.25} />
            <stop offset="100%" stopColor={seriesColor(0)} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="var(--grid)" vertical={false} />
        <XAxis
          dataKey="hour"
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={{ stroke: 'var(--axis)' }}
          tickFormatter={(h: number) => (h % 3 === 0 ? hourLabel(h) : '')}
          interval={0}
        />
        <YAxis hide domain={[0, 100]} />
        <Tooltip
          cursor={{ stroke: 'var(--axis)' }}
          content={({ active, payload, label }) =>
            active && payload?.length ? (
              <TooltipShell
                title={`${hourLabel(Number(label))} server time`}
                rows={[
                  {
                    label: 'Relative activity',
                    value: `${payload[0]?.value}%`,
                    color: seriesColor(0),
                  },
                ]}
              />
            ) : null
          }
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={seriesColor(0)}
          strokeWidth={2}
          fill="url(#fill-activity)"
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/**
 * Diverging delta indicator for a table cell: blue to the left of centre means
 * under market, red to the right means over. Gray is the neutral midpoint, and
 * the number beside it carries the value so the bar is reinforcement, not the
 * only channel.
 */
export function DeltaBar({
  pct,
  threshold = 10,
  label = 'market',
}: {
  pct: number;
  threshold?: number;
  label?: string;
}): ReactNode {
  const clamped = Math.max(-100, Math.min(100, pct));
  const width = Math.min(50, (Math.abs(clamped) / 60) * 50);
  const over = clamped > 0;
  const inBand = Math.abs(clamped) <= threshold;
  const color = inBand ? 'var(--axis)' : over ? 'var(--status-critical)' : 'var(--series-1)';

  return (
    <span
      className="delta-bar"
      role="img"
      title={`Your price is ${Math.abs(pct).toFixed(1)}% ${over ? 'above' : 'below'} the ${label} price. Left of centre is cheaper than the market, right is dearer; grey means within ${threshold}%.`}
      aria-label={`${Math.abs(pct).toFixed(1)}% ${over ? 'above' : 'below'} ${label}`}
    >
      <span className="centre" />
      <span
        className="fill"
        style={{
          background: color,
          width: `${width}%`,
          left: over ? '50%' : `${50 - width}%`,
        }}
      />
    </span>
  );
}
